import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import IconCancel from '@mui/icons-material/Cancel';
import IconSave from '@mui/icons-material/Save';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNotify } from 'react-admin';
import { useResourceAccess } from '../../../hooks/useAccess';
import TasksHandler, { DelinquentRun, DelinquentRunStatus } from '../../../react-admin/providers/handlers/TasksHandler';
import { formatProgress } from './formatProgress';

const POLL_RUNNING_MS = 5_000;
const POLL_IDLE_MS = 30_000;

const STATUS_COLOR: Record<DelinquentRunStatus, 'warning' | 'success' | 'error'> = {
  running: 'warning',
  done: 'success',
  failed: 'error',
};

const formatTime = (iso?: string): string => (iso ? new Date(iso).toLocaleString() : '—');

// Polls the run list, re-fetching every 5s while a run is in progress and every 30s otherwise.
const useDelinquentRuns = () => {
  const [runs, setRuns] = useState<DelinquentRun[]>([]);
  const [running, setRunning] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const notify = useNotify();
  // Avoid re-creating the interval (and surfacing transient errors) on every poll.
  const notifiedError = useRef(false);

  const refresh = useCallback(async () => {
    try {
      const res = await TasksHandler.listDelinquentRuns();
      setRuns(res.runs ?? []);
      setRunning(Boolean(res.running));
      setLoaded(true);
      notifiedError.current = false;
    } catch (e: any) {
      if (!notifiedError.current) {
        notify(`Failed to load report runs: ${e?.message ?? e}`, { type: 'warning' });
        notifiedError.current = true;
      }
    }
  }, [notify]);

  useEffect(() => {
    refresh();
    const interval = setInterval(refresh, running ? POLL_RUNNING_MS : POLL_IDLE_MS);
    return () => clearInterval(interval);
  }, [refresh, running]);

  return { runs, running, loaded, refresh };
};

const useDelinquentTaskAction = ({
  setShow,
  onStarted,
}: {
  setShow: (flag: boolean) => void;
  onStarted: () => void;
}) => {
  const [loading, setLoading] = useState(false);
  const [includeCategoryA, setIncludeCategoryA] = useState(false);

  const notify = useNotify();

  const close = () => {
    setIncludeCategoryA(false);
    setLoading(false);
    setShow(false);
  };

  const startTask = async () => {
    setLoading(true);
    TasksHandler.startTask(
      'collections-delinquent',
      { includeCategoryA },
      () => {
        notify('Delinquent report started', { type: 'info' });
        onStarted();
        close();
      },
      (e: any) => {
        notify(e.toString(), { type: 'error' });
        close();
      }
    );
  };

  return {
    includeCategoryA,
    setIncludeCategoryA: (event: any) => setIncludeCategoryA(event.target.checked),
    startTask,
    close,
    loading,
  };
};

const RunResultCell = ({ run }: { run: DelinquentRun }) => {
  if (run.status === 'running') {
    return (
      <Stack direction="row" spacing={1} alignItems="center">
        <CircularProgress size={14} />
        <Typography variant="body2">{formatProgress(run)}</Typography>
      </Stack>
    );
  }
  if (run.status === 'failed') {
    return (
      <Tooltip title={run.errorMessage ?? 'Unknown error'}>
        <Typography variant="body2" color="error" noWrap sx={{ maxWidth: 240 }}>
          {run.errorMessage ?? 'Failed'}
        </Typography>
      </Tooltip>
    );
  }
  if (run.downloadUrl) {
    return (
      <Link href={run.downloadUrl} target="_blank" rel="noopener noreferrer">
        Download CSV
      </Link>
    );
  }
  return <Typography variant="body2">No file</Typography>;
};

const DelinquentRunsTable = ({ runs, loaded }: { runs: DelinquentRun[]; loaded: boolean }) => {
  if (!loaded) {
    return (
      <Box sx={{ mt: 2 }}>
        <CircularProgress size={18} />
      </Box>
    );
  }
  if (runs.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
        No report runs yet.
      </Typography>
    );
  }
  return (
    <Table size="small" sx={{ mt: 2 }}>
      <TableHead>
        <TableRow>
          <TableCell>Status</TableCell>
          <TableCell>Started</TableCell>
          <TableCell>Finished</TableCell>
          <TableCell>By</TableCell>
          <TableCell>Cat A</TableCell>
          <TableCell>Result</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {runs.map((run) => (
          <TableRow key={run.runId}>
            <TableCell>
              <Chip size="small" label={run.status} color={STATUS_COLOR[run.status]} />
            </TableCell>
            <TableCell>{formatTime(run.startedAt)}</TableCell>
            <TableCell>{formatTime(run.finishedAt)}</TableCell>
            <TableCell>{run.triggeredBy ?? '—'}</TableCell>
            <TableCell>{run.includeCategoryA ? 'Yes' : 'No'}</TableCell>
            <TableCell>
              <RunResultCell run={run} />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

export const DelinquentTaskAction = () => {
  const { TasksAdmin } = useResourceAccess();
  const isRo = !TasksAdmin();

  const [show, setShow] = useState(false);
  const { runs, running, loaded, refresh } = useDelinquentRuns();
  const { startTask, close, loading, includeCategoryA, setIncludeCategoryA } = useDelinquentTaskAction({
    setShow,
    onStarted: refresh,
  });

  // One run at a time — the Run button is disabled while a report is in progress.
  const runDisabled = isRo || running;

  return (
    <Box sx={{ minWidth: 480 }}>
      <Stack direction="row" spacing={2} alignItems="center">
        <Tooltip title={running ? 'A report is already running' : ''}>
          <span>
            <Button color="primary" onClick={() => setShow(true)} disabled={runDisabled} variant={'outlined'}>
              Create Delinquent Report
            </Button>
          </span>
        </Tooltip>
        {running && <Chip size="small" label="running" color="warning" />}
      </Stack>

      <DelinquentRunsTable runs={runs} loaded={loaded} />

      <Dialog fullWidth open={show} onClose={loading ? undefined : close} aria-label="Cancel">
        <DialogTitle>Create Delinquent Collections Report</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <span>
                This generates the delinquent collections CSV in the background.
                <br />
                <br />
                When it finishes, the configured recipients receive an email with a time-limited download link, and the
                file becomes downloadable from the table above.
              </span>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={<Checkbox checked={includeCategoryA} onChange={setIncludeCategoryA} id="includeCategoryA" />}
                label="Include Category A customers"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={close} disabled={loading}>
            <IconCancel />
            <span>Cancel</span>
          </Button>
          <Button onClick={startTask} disabled={loading || runDisabled}>
            <IconSave />
            <span>Run</span>
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
